import React, { Dispatch, SetStateAction, useEffect, useLayoutEffect, useState } from "react";
import { Option } from "react-dropdown";
import { useDispatch, useSelector } from "react-redux";

import {
    deleteFolders,
    fetchFolderDropOffLink,
    fetchMaxRetentionPeriodLimit,
    updateFolderNotifications,
} from "src/actionCreators/folderActionCreators";
import {
    DeleteIcon,
    DisabledNotificationBellIcon,
    DropOffIcon,
    EditIcon,
    NotificationBellIcon,
    ShareIcon,
} from "src/assets/images/svg/IconCollection";
import { CreateFolderModal } from "src/components/Common/Modal/CreateFolderModal";
import { CustomModal } from "src/components/Common/Modal/CustomModal";
import { AppNotifier } from "src/components/Common/Notification/AppNotifier";
import FolderDropOffModal from "src/components/Folders/FolderDropOffModal";
import { ShareFolderModal } from "src/components/Folders/ShareFolderModal";
import { FolderToolbarConstants } from "src/Core/Common/Constants";
import { ArchiveFolderType, FolderEventType } from "src/Core/Common/Enums";
import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";
import { enableHeaderAction, getDaysFromRetentionPeriodId, getRetentionOptions, isFolderOwner } from "src/helper/HelperFunctions";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { ApplicationState } from "src/store";

interface IFolderActionsPopUp {
    selectedFolders?: IFolderModel[];
    retentionOptions?: Option[];
    isInlineLevel?: boolean;
    currentFolder: IFolderModel;
    onHide?: () => void;
    setReload?: (value: boolean, folder?: IFolderModel) => void;
    goToParentFolder?: (value: boolean) => void;
    setFolderName?: (name: string) => void;
    setShowFolderActionPopup?: Dispatch<SetStateAction<boolean>>;
}

const FolderActionsPopUp: React.FC<IFolderActionsPopUp> = ({
    selectedFolders,
    isInlineLevel,
    setReload,
    onHide,
    goToParentFolder,
    setFolderName,
    setShowFolderActionPopup,
}) => {
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showShareFolderModal, setShowShareFolderModal] = useState(false);
    const retentionData = useSelector((appState: ApplicationState) => appState.folderState.retentionPeriodsData);
    const [retentionPeriods, setRetentionPeriods] = useState<Option[]>([]);

    const { messageOptions: messageOptionsSettings } = useSelector(
        (appState: ApplicationState) => appState.adminSettingsState
    );
    const { userSettings } = useSelector((appState: ApplicationState) => appState.userSettingsState);
    const [showFolderDropOffModal, setShowFolderDropOffModal] = useState(false);

    const user = useSelector((state: ApplicationState) => state.userProfile);
    const mailboxUserData = useSelector((state: ApplicationState) => state.commonState.mailboxUserData);
    const [reloadCount, setReloadCount] = useState(0);
    const dispatch = useDispatch();
    const CompanyFolderNotificationSettings = useSelector((appState: ApplicationState) => appState.adminSettingsState.folderNotificationSettings);
    const [isFolderNotificationActive, setIsFolderNotificationActive] = useState(
        selectedFolders[0]?.isFolderNotificationActive
    );
    useEffect(() => {
        dispatch(fetchFolderDropOffLink(selectedFolders[0].folderId));
    }, []);

    useLayoutEffect(() => {
        showRenameModal || showDeleteModal;
    }, [showRenameModal, showDeleteModal]);

    useEffect(() => {
        let folderRetention = retentionData.retentionPeriods;
        if (messageOptionsSettings.retentionSettings?.isUserAllowedToChangeFolderRetentionPeriod) {
            if (selectedFolders[0].parentId === 0) {
                const maxRetention = getDaysFromRetentionPeriodId(userSettings?.retentionSettings.defaultRetentionPeriods.folders
                    || messageOptionsSettings.retentionSettings.defaultRetentionPeriods.folders, retentionData.retentionPeriods);

                folderRetention = retentionData.retentionPeriods.filter(rp => rp.days <= maxRetention);
                setRetentionPeriods(getRetentionOptions(folderRetention));
            } else {
                dispatch(
                    fetchMaxRetentionPeriodLimit(selectedFolders[0].folderId, (result: number) => {
                        folderRetention = retentionData.retentionPeriods.filter(rp => rp.days <= result);
                        setRetentionPeriods(getRetentionOptions(folderRetention));
                    })
                );
            }
        }
    }, [reloadCount, retentionData.retentionPeriods.length, selectedFolders[0].folderId, userSettings?.retentionSettings.defaultRetentionPeriods, messageOptionsSettings.retentionPeriodList, messageOptionsSettings.retentionSettings]);

    useEffect(() => {
        setIsFolderNotificationActive(selectedFolders[0].isFolderNotificationActive);
    }, [selectedFolders[0]]);

    const handleFolderNotifications = () => {
        const currentNotificationSetting = selectedFolders[0].isFolderNotificationActive;
        const isUserOwnerOfFolder = isFolderOwner(selectedFolders[0], user, mailboxUserData);
        if (isUserOwnerOfFolder && CompanyFolderNotificationSettings?.isFolderNotificationEnabled) {
            setIsFolderNotificationActive(!currentNotificationSetting);
            dispatch(
                updateFolderNotifications(selectedFolders[0].folderId, !currentNotificationSetting, (result: boolean) =>
                    onFolderNotificationUpdateSuccess(result, currentNotificationSetting)
                )
            );
        }
    };

    const onFolderNotificationUpdateSuccess = (result: boolean, previousState?: boolean) => {
        if (result) {
            AppNotifier.Success(ToasterMessages.SUCCESS.UPDATE_FOLDER_NOTIFICATION);
            setReload(true, { ...selectedFolders[0], isFolderNotificationActive: !previousState });
        } else {
            AppNotifier.Error(ToasterMessages.ERROR.UPDATE_FOLDER_NOTIFICATION_FAILED);
            setIsFolderNotificationActive(previousState);
        }
    };

    const handleRenameModalSubmition = (folder: IFolderModel) => {
        setShowRenameModal(false);
        setReload(true, { ...folder });
        setReloadCount(reloadCount + 1);
        setFolderName && setFolderName(folder.folderName);
        onHide();
    };

    const handleDeleteIconClick = () => {
        setShowDeleteModal(true);
    };

    const handleOnSubmitDelete = () => {
        dispatch(
            deleteFolders(
                selectedFolders.map(f => f.folderId),
                result => {
                    if (result) {
                        AppNotifier.Success(
                            selectedFolders.length > 1
                                ? ToasterMessages.SUCCESS.DELETE_FOLDERS
                                : ToasterMessages.SUCCESS.DELETE_FOLDER
                        );
                        setReload(true);
                        goToParentFolder(isInlineLevel ? true : false);
                    }
                    setShowDeleteModal(false);
                    onHide();
                },
                ResourceIdLocators.MyFiles.MyFilesBtnDeleteFolder
            )
        );
    };

    const handleShareFolderModalHide = () => {
        setReload(true);
        setShowShareFolderModal(false);
        setShowFolderActionPopup && setShowFolderActionPopup(false);
    };

    const handleRenameModalHide = () => {
        setShowRenameModal(false);
        setShowFolderActionPopup && setShowFolderActionPopup(false);
    };

    const handleDeleteModalHide = () => {
        setShowDeleteModal(false);
        setShowFolderActionPopup && setShowFolderActionPopup(false);
    };

    const handleHideFolderDropOffModal = () => {
        setShowFolderDropOffModal(false);
    };

    return (
        <div className="folder-actions-container">
              {selectedFolders[0].folderType !== ArchiveFolderType.FOLDER && (
                <div
                    className={
                        !isFolderOwner(selectedFolders[0], user, mailboxUserData) ||
                        !CompanyFolderNotificationSettings?.isFolderNotificationEnabled
                            ? "folder-action-wrapper toolbar-icon-disabled"
                            : "folder-action-wrapper"
                    }
                    id={Date.now().toString()}
                    onClick={() => {
                        handleFolderNotifications();
                    }}
                >
                    {isFolderNotificationActive && CompanyFolderNotificationSettings?.isFolderNotificationEnabled ? (
                        <NotificationBellIcon
                            width={16}
                            height={16}
                            disabled={!isFolderOwner(selectedFolders[0], user, mailboxUserData)}
                        />
                    ) : (
                        <DisabledNotificationBellIcon
                            width={17}
                            height={17}
                            disabled={!isFolderOwner(selectedFolders[0], user, mailboxUserData)}
                        />
                    )}
                    <span>{FolderToolbarConstants.TITLE_FOLDER_NOTIFICATION}</span>
                </div>
            )}
            {
                <div
                    className={
                        selectedFolders[0].folderType === ArchiveFolderType.FOLDER
                            ? "folder-action-wrapper toolbar-icon-disabled"
                            : "folder-action-wrapper"
                    }
                    data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnCopyFolder}
                    onClick={() => {
                        selectedFolders[0].folderType !== ArchiveFolderType.FOLDER && setShowFolderDropOffModal(true);
                    }}
                    title={
                        selectedFolders[0].folderType === ArchiveFolderType.FOLDER
                            ? FolderToolbarConstants.TITLE_FOLDER_DROPOFF_DISABLED
                            : ""
                    }
                >
                    <DropOffIcon
                        width={14}
                        height={14}
                    />
                    <span>{FolderToolbarConstants.TITLE_FOLDER_DROPOFF}</span>
                </div>
            }
            {isFolderOwner(selectedFolders[0], user, mailboxUserData) && (
                <div
                    className={"folder-action-wrapper"}
                    title={FolderToolbarConstants.TITLE_SHARE}
                    onClick={() => {
                        setShowShareFolderModal(true);
                    }}
                    data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnShareFolder}
                >
                    <ShareIcon width={12} height={12} />
                    <span>Share Folder</span>
                </div>
            )}
            {enableHeaderAction(selectedFolders[0], user, mailboxUserData) ? (
                <div
                    className="folder-action-wrapper"
                    data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnRenameFolder}
                    onClick={() => {
                        setShowRenameModal(true);
                        setReloadCount(reloadCount + 1);
                    }}
                >
                    <EditIcon width={12} height={12} />
                    <span>Edit</span>
                </div>
            ) : (
                <div
                    className="folder-action-wrapper toolbar-icon-disabled"
                    data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnRenameFolder}
                >
                    <EditIcon width={12} height={12} />
                    <span>Edit</span>
                </div>
            )}

            {enableHeaderAction(selectedFolders[0], user, mailboxUserData) ? (
                <div
                    className="folder-action-wrapper"
                    data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnDeleteFolder}
                    onClick={() => {
                        handleDeleteIconClick();
                    }}
                >
                    <DeleteIcon width={12} height={12} />

                    <span>Delete</span>
                </div>
            ) : (
                <div
                    className="folder-action-wrapper toolbar-icon-disabled"
                    data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnDeleteFolder}
                >
                    <DeleteIcon width={12} height={12} />

                    <span>Delete</span>
                </div>
            )}

            {showShareFolderModal && (
                <ShareFolderModal
                    show={showShareFolderModal}
                    folder={selectedFolders[0]}
                    onHide={handleShareFolderModalHide}
                />
            )}

            {showFolderDropOffModal && (
                <FolderDropOffModal
                    show={showFolderDropOffModal}
                    folderId={selectedFolders[0]?.folderId}
                    folderName={selectedFolders[0]?.folderName}
                    onHide={handleHideFolderDropOffModal}
                />
            )}
            {showRenameModal && (
                <div
                    // Added below code to avoid Events fired inside a Modal getting hijacked by parent
                    // Space key press was getting hijacked by parent
                    onKeyDown={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                    onFocus={e => e.stopPropagation()}
                    onMouseOver={e => e.stopPropagation()}
                >
                    <CreateFolderModal
                        modalType={FolderEventType.Edit}
                        show={showRenameModal}
                        retentionOptions={retentionPeriods}
                        currentFolder={selectedFolders[0]}
                        onHide={handleRenameModalHide}
                        onSubmit={handleRenameModalSubmition}
                        renameBtnResourceId={ResourceIdLocators.MyFiles.MyFilesDrpDwnRenameFolder}
                    />
                </div>
            )}

            {showDeleteModal && (
                <CustomModal
                    show={showDeleteModal}
                    cancelButtonName="Cancel"
                    confirmButtonName="Confirm"
                    title={FolderToolbarConstants.TITLE_DELETE_FOLDER}
                    onSubmit={handleOnSubmitDelete}
                    onHide={handleDeleteModalHide}
                    isConfirmModal={true}
                >
                    <div>
                        <span>{FolderToolbarConstants.DELETE_FOLDER}</span>
                    </div>
                </CustomModal>
            )}
        </div>
    );
};

export default FolderActionsPopUp;
